import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { Award, ArrowRight, CheckCircle2, Flame, Circle, Sparkles, Lock, PlayCircle, FileText, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!user?.paid) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/progress");
        setProgress(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // SAFETY NET: student paid through Stripe but webhook/polling didn't unlock their account.
  // Hits /api/payments/sync which re-queries Stripe for their most recent transaction.
  const syncAccess = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/payments/sync");
      await refresh();
      if (data.paid) {
        toast.success("Access unlocked — welcome to APM!");
      } else {
        toast.error(data.reason || "Stripe still shows payment pending. Try again in 30 sec.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed. If you have a Stripe receipt, please reply to your enrollment email.");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Loading your dashboard…</div>
      </div>
    );
  }

  // --- Unpaid gate (soft paywall) ---
  if (!user?.paid) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-14" data-testid="dashboard-paywall">
          <div className="overline mb-3"><span className="red-bar" />Student dashboard</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Welcome, {user?.full_name?.split(" ")[0]}.</h1>
          <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
            Your account is ready. Unlock the full 70-hour curriculum, NYC S-98 review, AI Tutor, and
            certificate with a one-time enrollment.
          </p>

          <div className="mt-10 border-4 border-red-600 p-8 flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="overline">APM Full Enrollment</div>
              <div className="font-heading text-5xl font-bold tracking-tighter mt-2">$149</div>
              <div className="text-xs text-zinc-600 mt-2">One-time · Lifetime access</div>
            </div>
            <Link to="/enroll" data-testid="paywall-enroll-btn">
              <button className="btn-primary">
                <Lock className="w-4 h-4" /> Unlock course <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {[
              { n: "70", l: "Total hours" },
              { n: "6", l: "Modules + S-98 review" },
              { n: "50", l: "Final-exam questions" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-red-600 pl-4">
                <div className="font-heading text-3xl font-bold">{s.n}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mt-1">{s.l}</div>
              </div>
            ))}
          </div>

          {/* SAFETY NET: paid through Stripe but webhook didn't unlock */}
          <div className="mt-10 border-l-4 border-amber-500 bg-amber-50 p-5 sm:p-6" data-testid="paywall-sync-card">
            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-heading text-base font-bold tracking-tight mb-1">
                  Already paid but still locked?
                </div>
                <p className="text-sm text-zinc-700 leading-relaxed mb-3">
                  If you completed Stripe checkout and your card was charged, your access should unlock
                  automatically. If it didn't, click below to re-sync with Stripe.
                </p>
                <button
                  onClick={syncAccess}
                  disabled={syncing}
                  className="btn-outline"
                  data-testid="paywall-sync-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
                  {syncing ? "Syncing…" : "Sync my access"}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 text-sm text-zinc-500">
            <Link to="/profile" className="hover:text-red-600" data-testid="paywall-profile-link">View profile</Link>
          </div>
        </main>
      </div>
    );
  }

  const pct = progress?.overall_percent || 0;
  const finalBest = progress?.final_exam;

  // "Continue where you left off" — find first incomplete lesson + parent module
  const nextUp = (() => {
    if (!progress?.module_progress) return null;
    for (const m of progress.module_progress) {
      for (const l of m.lessons) {
        if (!l.completed) {
          return { module: m, lesson: l, fresh: progress.hours_completed === 0 };
        }
      }
    }
    return null; // all lessons complete
  })();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="overline mb-3"><span className="red-bar" />Student dashboard</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Welcome back, {user?.full_name?.split(" ")[0]}.</h1>
            <p className="text-zinc-600 mt-2 text-sm">Pick up where you left off, or jump straight to the final practice exam.</p>
          </div>
          {progress?.certificate_earned && (
            <Link to="/certificate" data-testid="view-certificate-link">
              <button className="btn-primary">
                <Award className="w-4 h-4" /> View certificate
              </button>
            </Link>
          )}
        </div>

        {/* Continue where you left off — prominent resume card */}
        {nextUp ? (
          <Link
            to={`/lesson/${nextUp.lesson.id}`}
            className="block mb-6 bg-zinc-950 text-white p-6 sm:p-8 hover:bg-zinc-900 transition-colors group relative overflow-hidden"
            data-testid="continue-card"
          >
            <div className="absolute top-0 left-0 w-1.5 h-full bg-red-600" />
            <div className="grid md:grid-cols-12 gap-5 items-center">
              <div className="md:col-span-8">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-red-500 mb-2 flex items-center gap-2">
                  <PlayCircle className="w-3.5 h-3.5" />
                  {nextUp.fresh ? "Start your journey" : "Continue where you left off"}
                </div>
                <div className="text-xs font-mono text-zinc-500 mb-2">
                  {nextUp.module.title} · {nextUp.lesson.hours} hr
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight leading-tight group-hover:text-red-500 transition-colors" data-testid="continue-lesson-title">
                  {nextUp.lesson.title}
                </h3>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <span className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-3 font-semibold text-sm group-hover:bg-red-500 transition-colors" data-testid="continue-cta">
                  {nextUp.fresh ? "Begin lesson" : "Resume lesson"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </Link>
        ) : (
          // All lessons complete: nudge to final exam if not yet passed
          !finalBest?.passed && (
            <Link
              to="/final-exam"
              className="block mb-6 bg-zinc-950 text-white p-6 sm:p-8 hover:bg-zinc-900 transition-colors group relative overflow-hidden"
              data-testid="continue-card-allcomplete"
            >
              <div className="absolute top-0 left-0 w-1.5 h-full bg-red-600" />
              <div className="grid md:grid-cols-12 gap-5 items-center">
                <div className="md:col-span-8">
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-red-500 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5" /> All lessons complete
                  </div>
                  <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight leading-tight group-hover:text-red-500 transition-colors">
                    Take the 50-question final practice exam
                  </h3>
                  <p className="text-xs font-mono text-zinc-400 mt-2">120 min · 70% to pass · Earns your APM credential</p>
                </div>
                <div className="md:col-span-4 flex md:justify-end">
                  <span className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-3 font-semibold text-sm group-hover:bg-red-500 transition-colors">
                    Start exam <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          )
        )}

        {/* Summary */}
        <div className="grid md:grid-cols-4 gap-4 mb-10">
          <div className="card-tech p-6 md:col-span-2" data-testid="progress-card">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="overline">Overall progress</div>
              <Link to="/transcript" className="text-[10px] font-mono uppercase tracking-widest text-red-600 hover:underline inline-flex items-center gap-1" data-testid="dashboard-transcript-link">
                <FileText className="w-3 h-3" /> Transcript
              </Link>
            </div>
            <div className="flex items-end gap-3 mb-1">
              <div className="font-heading text-5xl font-bold tracking-tight" data-testid="dashboard-combined-pct">
                {progress?.combined_percent ?? pct}%
              </div>
              <div className="font-mono text-xs text-zinc-500 pb-2" data-testid="dashboard-combined-hrs">
                {progress?.combined_hours_completed ?? progress?.hours_completed} / {progress?.combined_hours_total ?? progress?.total_hours} hrs
              </div>
            </div>
            <Progress value={progress?.combined_percent ?? pct} className="h-2 rounded-none bg-zinc-100 [&>div]:bg-red-600" />
            <div className="grid grid-cols-2 gap-3 mt-3 text-[11px] font-mono">
              <div className="text-zinc-500">
                NICET: <span className="text-zinc-900 font-semibold">{progress?.hours_completed} / {progress?.total_hours} h</span>
              </div>
              <div className="text-zinc-500 text-right">
                Tracks: <span className="text-zinc-900 font-semibold">{progress?.track_hours_completed ?? 0} / {progress?.track_hours_total ?? 0} h</span>
              </div>
            </div>
          </div>
          <div className="card-tech p-6" data-testid="exam-card">
            <div className="overline mb-2">Final exam</div>
            {finalBest ? (
              <>
                <div className="font-heading text-3xl font-bold">{finalBest.percentage}%</div>
                <div className={`text-xs font-semibold mt-1 ${finalBest.passed ? "text-green-700" : "text-red-600"}`}>
                  {finalBest.passed ? "PASSED" : "RETAKE NEEDED"}
                </div>
              </>
            ) : (
              <>
                <div className="font-heading text-2xl font-bold text-zinc-400">Not attempted</div>
                <div className="text-xs text-zinc-500 mt-1 font-mono">50 questions · 70% to pass</div>
              </>
            )}
            <Link to="/final-exam" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700" data-testid="go-to-final-exam-link">
              {finalBest ? "Retake exam" : "Start exam"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="card-tech p-6" data-testid="tutor-card">
            <div className="overline mb-2">AI Tutor</div>
            <div className="font-heading text-xl font-bold tracking-tight">Claude Sonnet 4.5</div>
            <div className="text-xs text-zinc-500 mt-1">Open from any lesson to ask NFPA 72 questions on demand.</div>
            <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-red-600">
              <Sparkles className="w-4 h-4" /> Available in every lesson
            </div>
          </div>
        </div>

        {/* Specialty Tracks call-out */}
        <Link to="/tracks" className="block card-tech p-6 mb-4 group border-red-200" data-testid="dashboard-tracks-card">
          <div className="grid md:grid-cols-12 gap-5 items-center">
            <div className="md:col-span-1">
              <div className="w-10 h-10 bg-purple-600 text-white grid place-items-center font-heading font-bold text-xs">+7</div>
            </div>
            <div className="md:col-span-8">
              <div className="overline mb-1">7 specialty tracks · 125 additional hours · included free</div>
              <div className="font-heading text-xl font-bold tracking-tight group-hover:text-red-600 transition-colors">
                FDNY S-95 · F-53 · S-12 · S-13 · BMS+Elevator · Smoke+EVACS · Mfr Exposure
              </div>
              <div className="text-xs text-zinc-600 mt-1">Each track issues its own credential when you pass the final exam.</div>
            </div>
            <div className="md:col-span-3 text-right">
              <ArrowRight className="w-5 h-5 ml-auto text-zinc-400 group-hover:text-red-600 transition-all group-hover:translate-x-1" />
            </div>
          </div>
        </Link>

        {/* Lab call-out */}
        <Link to="/lab" className="block card-tech p-6 mb-4 group border-red-200" data-testid="dashboard-lab-card">
          <div className="grid md:grid-cols-12 gap-5 items-center">
            <div className="md:col-span-1">
              <div className="w-10 h-10 bg-red-600 text-white grid place-items-center font-heading font-bold">LAB</div>
            </div>
            <div className="md:col-span-8">
              <div className="overline mb-1">Interactive practice tools</div>
              <div className="font-heading text-xl font-bold tracking-tight group-hover:text-red-600 transition-colors">
                Riser diagrams + FACU panel simulator
              </div>
              <div className="text-xs text-zinc-600 mt-1">4 building risers · 10 panel scenarios · Free-play mode · Drill alarm response sequences</div>
            </div>
            <div className="md:col-span-3 text-right">
              <ArrowRight className="w-5 h-5 ml-auto text-zinc-400 group-hover:text-red-600 transition-all group-hover:translate-x-1" />
            </div>
          </div>
        </Link>

        {/* NYC S-98 call-out */}
        <Link to="/s98-review" className="block card-tech p-6 mb-10 group" data-testid="dashboard-s98-card">
          <div className="grid md:grid-cols-12 gap-5 items-center">
            <div className="md:col-span-1">
              <div className="w-10 h-10 bg-red-600 text-white grid place-items-center font-heading font-bold">NYC</div>
            </div>
            <div className="md:col-span-8">
              <div className="overline mb-1">NYC review module</div>
              <div className="font-heading text-xl font-bold tracking-tight group-hover:text-red-600 transition-colors">
                FDNY S-98 Certificate of Fitness — Review
              </div>
              <div className="text-xs text-zinc-600 mt-1">NYC Fire Code Ch. 9 · NYC Electrical Code · 20-question FDNY-style practice quiz</div>
            </div>
            <div className="md:col-span-3 text-right">
              <ArrowRight className="w-5 h-5 ml-auto text-zinc-400 group-hover:text-red-600 transition-all group-hover:translate-x-1" />
            </div>
          </div>
        </Link>

        {/* Modules */}
        <div className="mb-6">
          <div className="overline mb-3"><span className="red-bar" />Modules</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Your curriculum</h2>
        </div>

        <div className="grid gap-4">
          {progress?.module_progress?.map((m, idx) => (
            <Link
              to={`/module/${m.id}`}
              key={m.id}
              className="card-tech p-6 grid md:grid-cols-12 gap-4 items-center group"
              data-testid={`dashboard-module-${m.id}`}
            >
              <div className="md:col-span-1">
                <div className="w-10 h-10 bg-red-50 text-red-600 grid place-items-center font-heading font-bold">
                  M{idx + 1}
                </div>
              </div>
              <div className="md:col-span-5">
                <div className="font-heading text-xl font-bold tracking-tight group-hover:text-red-600 transition-colors">{m.title}</div>
                <div className="text-xs text-zinc-500 font-mono mt-1">{m.hours_completed} / {m.hours} hrs · {m.lessons.length} lesson{m.lessons.length > 1 ? "s" : ""}</div>
              </div>
              <div className="md:col-span-5">
                <Progress value={m.percent} className="h-2 rounded-none bg-zinc-100 [&>div]:bg-red-600" />
                <div className="flex items-center gap-3 mt-2">
                  {m.lessons.map((l) => (
                    <div key={l.id} className="flex items-center gap-1 text-xs text-zinc-600">
                      {l.completed ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Circle className="w-3.5 h-3.5 text-zinc-300" />}
                      <span className="truncate max-w-[120px]">{l.title}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-1 text-right">
                <ArrowRight className="w-5 h-5 ml-auto text-zinc-400 group-hover:text-red-600 transition-all group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 border-t border-zinc-200 pt-8 flex items-center justify-between flex-wrap gap-4 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-red-600" />
            <span>Stay focused. Every lesson moves you closer to your NICET certificate.</span>
          </div>
        </div>
      </main>
    </div>
  );
}
